import Vue from "vue";
import VueRouter from "vue-router";
import waitFor from "@/utils/waitFor";
import store from "@/store";
import signin from "@/router/reports/signin";
import signout from "@/router/reports/signout";
import version from "@/router/reports/version";
import organizations from "@/router/reports/organizations";
import users from "@/router/reports/users";
import reports from "@/router/reports/reports";
import investigations from "@/router/reports/investigations";
import notFound from "@/router/reports/not-found";
import policies from "@/router/reports/policies";
import admin from "@/router/reports/admin";
import { resolveReactRedirect } from "@/router/reports/react-redirect";
import signedUrl from "@/utils/signedUrl";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    component: () => import("@/views/reports/Home.vue"),
    meta: {
      authorization: { policies: ["authenticated"] },
    },
  },
  {
    path: "/help/video/:host/:videoId",
    props: route => ({ host: route.params.host, videoId: route.params.videoId, h: route.query.h }),
    component: () => import("@/views/reports/HelpVideo.vue"),
    meta: {
      authorization: { policies: ["authenticated"] },
    },
  },
  ...signin,
  ...signout,
  ...version,
  ...organizations,
  ...users,
  ...reports,
  ...investigations,
  ...admin,
  ...notFound,
];

const router = new VueRouter({
  mode: "history",
  routes,
});

router.beforeEach(async (to, from, next) => {
  // state
  await waitFor(() => store.getters["isInitialized"]);

  if (store.getters["isAuthenticated"]) {
    await waitFor(() => store.getters["user/user"]);
    await waitFor(() => store.getters["organization/organizations"]);
    await waitFor(() => store.getters["role/roles"]);
    await waitFor(() => store.getters["feature/features"]);

    // react console
    // Only inside the authenticated branch: the store state this reads is populated by
    // the waitFor calls above, so an anonymous visit to /signin can never redirect.
    //
    // Both settings are resolved for the organization the TARGET route names. Vuex's
    // "current" organization is just payload[0], so for a multi-org user visiting any
    // other organization it answers about the wrong one — in both directions. Routes
    // without an organization param (the home route) fall back to the current one.
    const targetOrganizationId = to.params.organizationId || null;

    const reactConsoleUrl = resolveReactRedirect({
      effectiveUi: store.getters["organization/effectiveUiFor"](targetOrganizationId),
      hasReactConsole: targetOrganizationId ? store.getters["feature/hasReactConsoleFor"](targetOrganizationId) : store.getters["feature/hasReactConsole"],
      fullPath: to.fullPath,
      isSignedUrlSession: signedUrl.isSignedUrl(),
    });

    if (reactConsoleUrl) {
      // replace(), not assign(): the legacy URL we are leaving should not sit in history
      // behind the console, or Back lands here and redirects again.
      window.location.replace(reactConsoleUrl);
      next(false);
      return;
    }
  }

  // policies
  policies.run(to, from, next);
});

export default router;
